import { useRef } from 'react';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { MyLink } from '@/components/MyLink';
import { CenterModalWrapper } from '@/components/ModalTypes/center';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { useApplyForRefin } from '@/query/queries/loan/useApplyForRefin';
import { prettyFloatMoney } from '@/helpers/utils';
import { APP_LINKS } from '@/helpers/links';
import { prettyDate } from '@/helpers/date';
import Notify from '@/components/Notification';
import { DATE_FORMAT_DD_MMMM, DATE_FORMAT_MMMM_YYYY } from '@/helpers/common';
import IconClose from './close.svg';
import s from './index.module.scss';

export const ApplyRefinModal = ({ loan, onSuccess, onClose }) => {
  const { mutateAsync: apply, isPending } = useApplyForRefin();
  const ref = useRef(null);
  const { t } = useTranslation('modal');

  useOutsideClick(ref, onClose, true);

  const onApply = async () => {
    const payload = {
      fundraising: loan.fundraising_id,
      refinancing_option: loan.refinancing.available_option_id,
    };
    const res = await apply(payload);
    if (res?.last_payment_amount) {
      onSuccess && onSuccess();
      loan?.onSuccess && loan.onSuccess(loan);
      Notify.success({ text: t('apply_refin_modal.apply_successful') });
    }
  };

  return (
    <CenterModalWrapper onClose={onClose}>
      <div className={s.wrapper} ref={ref}>
        <img
          src={IconClose}
          alt="close"
          width={12}
          height={12}
          className={s.close_btn}
          onClick={onClose}
        />
        <div className={s.title}>{t('apply_refin_modal.title')}</div>
        <div className={classnames('f-body', s.desc)}>
          <Trans
            ns="modal"
            i18nKey="apply_refin_modal.apply_last_loan"
            values={{
              lastPaymentAmount: prettyFloatMoney(loan.refinancing.last_payment_amount),
              startDate: prettyDate(loan.refinancing.fundraising_start_date, DATE_FORMAT_MMMM_YYYY),
            }}
            components={{
              Span: <span className="f-700" />,
            }}
          />
        </div>
        {loan.refinancing?.refin_fee_before ? (
          <div className={classnames('f-body', s.fee)}>
            <Trans
              ns="modal"
              i18nKey="apply_refin_modal.apply_by_until_date"
              values={{
                feeBefore: loan.refinancing.refin_fee_before,
                feeAfter: loan.refinancing?.refin_fee_after,
                validUntil: prettyDate(loan.refinancing.valid_until_date, DATE_FORMAT_DD_MMMM),
              }}
              components={{
                Span: <span className="f-700" />,
              }}
            />
          </div>
        ) : (
          <div className={classnames('f-body', s.fee)}>
            <Trans
              ns="modal"
              i18nKey="apply_refin_modal.late_refin_fee"
              values={{
                feeLate: loan.refinancing?.refin_fee_late,
              }}
              components={{
                Span: <span className="f-700" />,
              }}
            />
          </div>
        )}
        <div className={s.note}>
          {t('apply_refin_modal.by_submitting_a_refinancing_application')}
        </div>
        <div className={s.controls}>
          <MyLink href={APP_LINKS.loanTermsRefinancing()} isExternal newWindow className={s.learn_btn}>
            {t('apply_refin_modal.learn_more')}
          </MyLink>
          <button className={s.apply_btn} type="button" onClick={onApply}>
            {isPending
              ? t('apply_refin_modal.processing')
              : t('apply_refin_modal.apply_for_refinance')}
          </button>
        </div>
      </div>
    </CenterModalWrapper>
  );
};
